import { useEffect } from 'react';

import { useEntityPreviewData } from '@work4all/components/lib/components/entity-preview/hooks/use-entity-preview-data';
import { TicketPreview } from '@work4all/components/lib/components/entity-preview/ticket-preview/TicketPreview';
import { TicketPreviewFields } from '@work4all/components/lib/components/entity-preview/ticket-preview/TicketPreviewFields';
import { EntityPreviewContainerProps } from '@work4all/components/lib/components/entity-preview/types';

import { Ticket } from '@work4all/models/lib/Classes/Ticket.entity';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { useEntityActions } from '../../../../../../components/data-tables/entity-table/EntityActionProvider';
import {
  useYellowNoteContext,
  YellowNoteContext,
} from '../../yellow-note/yellow-note-context';
import { YellowNoteCard } from '../../yellow-note/YellowNoteCard';
import { useTicketCustomActions } from '../use-ticket-custom-actions';

type TicketPreviewContainerProps = EntityPreviewContainerProps<string>;

export function TicketPreviewContainer(props: TicketPreviewContainerProps) {
  const { ids: ticketIds, isPartnerPage, ...handlers } = props;
  const tickets = useEntityPreviewData<Ticket>(
    ticketIds,
    Entities.ticket,
    TicketPreviewFields
  );

  const { actions, emailPopover } = useTicketCustomActions({
    tickets,
  });

  const { addActions } = useEntityActions();

  useEffect(() => {
    addActions({ left: actions });
  }, [actions, addActions]);

  if (!tickets) {
    return null;
  }

  const yellowNoteData = tickets[0].businessPartner?.data?.postIt;
  const hasYellowNoteData =
    tickets.length === 1 && yellowNoteData?.body?.length;

  if (isPartnerPage) {
    return (
      <TicketPreviewFromFile
        handlers={handlers}
        tickets={tickets}
        hasYellowNoteData={hasYellowNoteData}
        emailPopover={emailPopover}
      />
    );
  }

  return (
    <TicketPreviewWithYellowNote
      handlers={handlers}
      tickets={tickets}
      yellowNoteData={yellowNoteData}
      hasYellowNoteData={hasYellowNoteData}
      emailPopover={emailPopover}
    />
  );
}

function TicketPreviewFromFile({
  handlers,
  tickets,
  hasYellowNoteData,
  emailPopover,
}) {
  const { isOpen } = useYellowNoteContext();
  const hideYellowNote = !hasYellowNoteData || (hasYellowNoteData && isOpen);

  return (
    <>
      {emailPopover}
      <TicketPreview
        {...handlers}
        entries={tickets}
        YellowNote={!hideYellowNote ? <YellowNoteCard /> : null}
      />
    </>
  );
}

function TicketPreviewWithYellowNote({
  handlers,
  tickets,
  yellowNoteData,
  hasYellowNoteData,
  emailPopover,
}) {
  // Add/Delete actions not allowed for the Ticket' YellowNote
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const asyncNoop = async () => {};

  return (
    <YellowNoteContext
      data={yellowNoteData ?? null}
      updateYellowNote={asyncNoop}
      deleteYellowNote={asyncNoop}
    >
      {emailPopover}
      <TicketPreview
        {...handlers}
        entries={tickets}
        YellowNote={hasYellowNoteData ? <YellowNoteCard /> : null}
      />
    </YellowNoteContext>
  );
}
